import { Injectable } from '@angular/core';
import {
  NotificationRef,
  NotificationService,
} from '@progress/kendo-angular-notification';
import { Globals } from 'src/app/common/globals';
import { CookieService } from 'ngx-cookie-service';
import { EmailMaskPipe } from 'src/app/common/pipes/email-mask.pipe';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  constructor(
    private notificationService: NotificationService,
    public globals: Globals,
    private cookieService: CookieService,
    private http: HttpClient
  ) { }

  applicationListAdvancedFilter: any = null;
  applicationListGridSort: any = null;
  applicationListGridSkip: any = null;
  applicationListGridPageSize: any = null;
  applicationListSearchText: any = null;

  userListAdvancedFilter: any = null;
  userListGridSort: any = null;
  userListGridSkip: any = null;
  userListGridPageSize: any = null;
  userListSearchText: any = null;

  resultListAdvancedFilter: any = null;
  resultListGridSort: any = null;
  resultListGridSkip: any = null;
  resultListGridPageSize: any = null;
  resultListSearchText: any = null;

  userReportFilters: any = null;
  userReportAdvancedFilter: any = null;
  userReportAdvancedFilterProp: any = null;
  userReportStatus: any = null;
  userReportStatusName: any = null;

  applicationReportFilters: any = null;
  applicationReportAdvancedFilter: any = null;
  applicationReportAdvancedFilterProp: any = null;
  applicationReportStatus: any = null;
  applicationReportStatusName: any = null;

  examReportFilters: any = null;
  examReportAdvancedFilter: any = null;
  examReportAdvancedFilterProp: any = null;
  examReportStatus: any = null;
  examReportStatusName: any = null;

  ceuReportFilters: any = null;

  showCommonSuccessNotification(msg: string) {
    this.showNotification('success', msg);
  }

  showCommonErrorNotification(
    msg: string = 'Something went wrong! Please try again.',
    hideAfter: number = 5000
  ) {
    this.showNotification('error', msg, hideAfter);
  }

  showNotification(
    type: notificationType,
    msg: string,
    hideAfter: number = 5000
  ) {
    if (this.globals.lastNotification) {
      this.globals.lastNotification.hide();
    }
    const notification: NotificationRef = this.notificationService.show({
      content: msg,
      // closable: type == "error" ? true : false,
      hideAfter: hideAfter,
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'fade', duration: 400 },
      type: { style: type, icon: true },
    });
    this.globals.lastNotification = notification;
  }

  showLoader() {
    this.globals.isLoading = true;
  }

  hideLoader() {
    this.globals.isLoading = false;
  }

  setCookie(name: string, data: any, encoded: boolean = true) {
    this.cookieService.set(
      encoded ? window.btoa(name) : name,
      encoded ? window.btoa(JSON.stringify(data)) : data,
      365,
      '/',
      this.globals.cookieDomainUrl
    );
  }

  setSamlCookie(name: string, data: any, expireInMinutes = 43800) {
    this.cookieService.set(
      name,
      data,
      expireInMinutes / 1440,
      '/',
      this.globals.cookieDomainUrl
    );
  }

  getCookie(name: string) {
    return this.cookieService.get(window.btoa(name))
      ? JSON.parse(window.atob(this.cookieService.get(window.btoa(name))))
      : null;
  }

  clearLoginCookie() {
    this.globals.impersonateId = null;
    this.clearCookie('access_token');
    this.clearCookie('logged_in_user_info');
    this.clearCookie('current_role');
    this.clearCookie('token_type');
    this.clearCookie('saml_access_token');
    this.clearCookie('impersonate_id');
    this.clearCookie('impersonate_by');
    this.clearCookie('impersonate_by_last_page');
    this.clearCookie('web_login_token', false);
    this.clearCookie('saml_access_token', false);
    this.clearCookie('session_expire_minutes', false);
    this.clearCookie('manage-users-list');
    this.clearCookie('assign-users-list');
    this.clearCookie('app_version');
  }

  clearCookie(name: string, encoded: boolean = true) {
    this.cookieService.delete(
      encoded ? window.btoa(name) : name,
      '/',
      this.globals.cookieDomainUrl
    );
  }

  setPaginationCookie(name: string, data: any) {
    if (typeof window !== 'undefined') {
      this.cookieService.set(
        window.btoa(name),
        window.btoa(JSON.stringify(data)),
        1,
        '/',
        this.globals.cookieDomainUrl
      );
    }
  }

  getPaginationCookie(name: string) {
    if (typeof window !== 'undefined') {
      return this.cookieService.get(window.btoa(name))
        ? JSON.parse(window.atob(this.cookieService.get(window.btoa(name))))
        : null;
    } else {
      return null;
    }
  }

  clearPaginationCookie(name: string) {
    if (typeof window !== 'undefined') {
      if (this.cookieService.get(window.btoa(name))) {
        this.cookieService.delete(
          window.btoa(name),
          '/',
          this.globals.cookieDomainUrl
        );
      }
    }
  }

  syncAuthGlobals() {
    this.globals.authData = this.cookieService.get(
      window.btoa('logged_in_user_info')
    )
      ? JSON.parse(
        window.atob(
          this.cookieService.get(window.btoa('logged_in_user_info'))
        )
      )
      : null;



    this.globals.userCurrentRole = this.cookieService.get(
      window.btoa('current_role')
    )
      ? JSON.parse(
        window.atob(this.cookieService.get(window.btoa('current_role')))
      )
      : null;

    if (this.globals.authData == null) {
      this.showCommonErrorNotification();
    }
    // this.globals.browserInfo.user_id = this.globals.authData.id;

    setTimeout(() => {
      this.globals.isLoggedIn = this.globals.authData ? true : false;
    }, 500);
    this.globals.selectedFacility = this.globals.authData ? this.globals.authData.facility_details : null;

    this.syncRole();

    return this.globals.authData ? true : false;
  }

  syncRole() {
    this.globals.isItAdmin = this.globals.userCurrentRole.slug === 'it-admin';
    this.globals.isAdmin = this.globals.userCurrentRole.slug === 'admin';
    this.globals.isCandidate =
      this.globals.userCurrentRole.slug === 'candidate';
    this.globals.isAuditor = this.globals.userCurrentRole.slug === 'auditor';
    this.globals.isCredentialManager =
      this.globals.userCurrentRole.slug === 'credential-manager';
    this.globals.isAccountant =
      this.globals.userCurrentRole.slug === 'accountant';
    this.globals.isFacility =
      this.globals.userCurrentRole.slug === 'facility';
  }

  maskEmail(email: string) {
    return new EmailMaskPipe().transform(email);
  }

  copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.showCommonSuccessNotification('"' + text + '" Copied!');
      })
      .catch();
  }

  addDays(date: Date, days: number) {
    date.setDate(date.getDate() + days);
    return date;
  }

  browserDetectLog() {
    const entity = this.globals.browserInfo;
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(this.globals.baseAPIUrl + '/add/browser-logs', entity)
      ).then(
        (res: any) => { },
        (msg) => { }
      );
    });
  }

  getUILinkCommonData() {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.get(this.globals.baseAPIUrl + '/ui-content-get-list/1/1')
      ).then(
        (res: any) => {
          this.globals.uiLinkCommonData = res['data'];
          this.globals.instagramLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'instagramLink'
          );
          this.globals.facebookLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'facebookLink'
          );
          this.globals.chcLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'chcLink'
          );
          this.globals.recertificationHandbookLink =
            this.globals.checkKeyValueInLinks(
              res['data'],
              'recertificationHandbookLink'
            );
          this.globals.examFeedbackLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'examFeedbackLink'
          );
          this.globals.examScheduleLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'examScheduleLink'
          );
          this.globals.linkedinLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'linkedinLink'
          );
          this.globals.supportMailLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'supportMailLink'
          );
          this.globals.westminsterInsuranceLink =
            this.globals.checkKeyValueInLinks(
              res['data'],
              'westminsterInsuranceLink'
            );
          this.globals.twitterLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'twitterLink'
          );
          this.globals.telephone = this.globals.checkKeyValueInLinks(
            res['data'],
            'telephone'
          );
          this.globals.examHandbookLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'examHandbookLink'
          );
          this.globals.examResourcesLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'examResourcesLink'
          );
          this.globals.donationLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'donationLink'
          );
          this.globals.retireFormLink = this.globals.checkKeyValueInLinks(
            res['data'],
            'retireFormLink'
          );
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }

  getConfByKey(key: string) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.get(this.globals.baseAPIUrl + '/config/' + key)
      ).then(
        (res) => {
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }

  getUIContentCommonData() {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.get(this.globals.baseAPIUrl + '/ui-content-get-list/2/1')
      ).then(
        (res) => {
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }

  getApplicationStepStatusClass(status: string): any {
    var commonClasses = 'icon-status text-status ';
    if (
      status == 'not-submitted' ||
      status == 'audit-selected' ||
      status == 'pending'
    ) {
      return commonClasses + 'incomplete';
    } else if (status == 'submitted' || status == 'created') {
      return commonClasses + 'complete';
    } else if (
      status == 'audit-under-review' ||
      status == 'audit-partial-verified' ||
      status == 'audit-docs-submitted'
    ) {
      return commonClasses + 'inreview';
    } else if (
      status == 'audit-passed' ||
      status == 'auto-verified' ||
      status == 'portfolio-passed' ||
      status == 'exam-passed'
    ) {
      return commonClasses + 'verified';
    } else if (status == 'audit-additional-docs-needed') {
      return commonClasses + 'correction';
    } else if (
      status == 'audit-failed' ||
      status == 'audit-failed-due-to-parent' ||
      status == 'portfolio-failed' ||
      status == 'auto-cancelled' ||
      status == 'cancelled' ||
      status == 'expired' ||
      status == 'exam-failed'
    ) {
      return commonClasses + 'denied';
    }
  }

  getApplicationStatusClass(status: string): any {
    var commonClasses = '';
    if (
      status == 'in-progress' ||
      status == 'draft' ||
      status == 'recert-draft' ||
      status == 'recert-in-progress' ||
      status == 'pending' ||
      status == 'created'
    ) {
      return commonClasses + 'incomplete';
    } else if (
      status == 'submitted' ||
      status == 'auto-verified-pending-scheduling' ||
      status == 'audit-passed-pending-scheduling' ||
      status == 'exam-completed'
    ) {
      return commonClasses + 'complete';
    } else if (
      status == 'audit-selected' ||
      status == 'audit-docs-submitted' ||
      status == 'audit-under-review' ||
      status == 'auto-verified-pending-portfolio-review' ||
      status == 'audit-passed-pending-portfolio-review' ||
      status == 'exam-booked' ||
      status == 'exam-booking-rescheduled'
    ) {
      return commonClasses + 'inreview';
    } else if (
      status == 'auto-verified' ||
      status == 'audit-passed' ||
      status == 'portfolio-passed' ||
      status == 'exam-passed'
    ) {
      return commonClasses + 'verified';
    } else if (status == 'audit-additional-docs-needed') {
      return commonClasses + 'correction';
    } else if (
      status == 'audit-failed' ||
      status == 'audit-failed-due-to-parent' ||
      status == 'portfolio-failed' ||
      status == 'auto-cancelled' ||
      status == 'cancelled' ||
      status == 'expired' ||
      status == 'exam-failed' ||
      status == 'excused-absent' ||
      status == 'absent' ||
      status == 'exam-booking-cancelled'
    ) {
      return commonClasses + 'denied';
    }
  }

  updateConfiguration(id: string, entity: any) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(this.globals.baseAPIUrl + '/config/update/' + id, entity)
      ).then(
        (res) => {
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }

  showAuditLegends(status: string): boolean {
    const hiddenStatuses = [
      'in-progress',
      'draft',
      'recert-draft',
      'recert-in-progress',
      'submitted',
      'auto-verified',
      'auto-verified-pending-scheduling',
      'auto-verified-pending-portfolio-review',
    ];
    return !hiddenStatuses.includes(status);
  }

  showCandidateActionColumn(status: string): boolean {
    const showStatuses = [
      'in-progress',
      'draft',
      'recert-draft',
      'recert-in-progress',
    ];
    return showStatuses.includes(status);
  }

  showCandidateAuditColumn(status: string): boolean {
    const hiddenStatuses = [
      'in-progress',
      'draft',
      'recert-draft',
      'recert-in-progress',
      'submitted',
      'auto-verified',
      'cancelled',
      'auto-cancelled',
      'auto-verified-pending-scheduling',
      'auto-verified-pending-portfolio-review',
    ];
    return !hiddenStatuses.includes(status);
  }

  showReviewerAuditActionColumn(status: string): boolean {
    const hiddenStatuses = [
      'in-progress',
      'draft',
      'recert-draft',
      'recert-in-progress',
      'submitted',
      'auto-verified',
      'auto-verified-pending-scheduling',
      'auto-verified-pending-portfolio-review',
      'cancelled',
      'auto-cancelled',
      'audit-selected',
    ];
    return !hiddenStatuses.includes(status);
  }

  showAuditStatusColumn(status: string): boolean {
    const hiddenStatuses = [
      'in-progress',
      'draft',
      'recert-draft',
      'recert-in-progress',
      'submitted',
      'auto-verified',
      'auto-verified-pending-scheduling',
      'auto-verified-pending-portfolio-review',
    ];
    return !hiddenStatuses.includes(status);
  }

  showAuditDueDeadline(status: string): boolean {
    const hiddenStatuses = [
      'audit-passed',
      'audit-passed-pending-scheduling',
      'audit-passed-pending-portfolio-review',
      'auto-verified',
      'auto-verified-pending-scheduling',
      'auto-verified-pending-portfolio-review',
      'portfolio-passed',
      'exam-passed',
      'exam-failed',
    ];
    return !hiddenStatuses.includes(status);
  }

  showExamButton(status: string): boolean {
    const showStatuses = [
      'auto-verified-pending-scheduling',
      'audit-passed-pending-scheduling',
      'exam-passed',
      'exam-failed',
      'auto-cancelled',
    ];
    return showStatuses.includes(status);
  }

  updateRecertFees(entity: any) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(this.globals.baseAPIUrl + '/update-exam-fees', entity)
      ).then(
        (res) => {
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }

  getSecurityQuestions(userId: any) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.get(
          this.globals.baseAPIUrl + '/user-security-question/' + userId
        )
      ).then(
        (res: any) => resolve(res),
        (err) => reject(err)
      );
    });
  }

  getUserListForImpersonation(searchText: string = '') {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(
          this.globals.baseAPIUrl + '/user-list-for-impersonation',
          {
            searchText: searchText
          }
        )
      ).then(
        (res: any) => resolve(res),
        (err) => reject(err)
      );
    });
  }

  redirectToLearnUponSSO() {
    this.getLearnUponSSOUrl().then(
      (data: any) => {
        const url = data.data;
        window.location.href = url;
      }
    );
  }
  getLearnUponSSOUrl() {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.get(
          this.globals.baseAPIUrl + '/learn-upon/sso-url'
        )
      ).then(
        (res: any) => resolve(res),
        (err) => reject(err)
      );
    });
  }

  getLatestVersion() {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.get(
          this.globals.baseAPIUrl + '/get-latest-version'
        )
      ).then(
        (res: any) => {
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }


  getTempPublicFileUrl(path: string) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(
          this.globals.baseAPIUrl + '/storage-file-view', { file_path: path }
        )
      ).then(
        (res: any) => resolve(res),
        (err) => reject(err)
      );
    });
  }

  isInternalProduct(category: string): boolean {
    const outsideVendorsCategories = [
      'amazon-prep-books',
      'printify-apparels-accessories',
    ];
    return !outsideVendorsCategories.includes(category);
  }

  isLMSProduct(category: string): boolean {
    const lmsCategories = [
      'lms-exam-prep-courses',
      'lms-professional-organization-activities',
      'lms-case-studies',
      'lms-games',
      'lms-additional-points-activity',
      'lms-reflections',
      'lms-ongoing-professional-development',
      'lms-nursing-continous-professional-development',
    ];
    return lmsCategories.includes(category);
  }

  isCMSProduct(category: string): boolean {
    const cmsCategories = [
      'cms-certifications',
      'cms-recertifications',
      'cms-exam-retakes',
      'cms-sd-certifications',
      'cms-sd-recertifications'
    ];
    return cmsCategories.includes(category);
  }

  moveArrayItemsToFront(
    array: { id: string | number | null; name: string }[],
    itemsToMove: string[]
  ) {
    // Create an array with the items to move
    const itemsFront = array.filter((item) => itemsToMove.includes(item.name));

    // Create an array with the remaining items
    const remainingItems = array.filter(
      (item) => !itemsToMove.includes(item.name)
    );

    // Concatenate the arrays, with the specific items at the front
    return itemsFront.concat(remainingItems);
  }

  getUniqueDropdownItems(data: ddInterface[]) {
    // Remove repeated categories by label and keep the first one and add all ids to the first one
    var unique = data.filter(
      (v: any, i: any, a: any) =>
        a.findIndex((t: ddInterface) => t.label === v.label) === i
    );
    unique.forEach((element: ddInterface) => {
      element.ids = data
        .filter((t: ddInterface) => t.label === element.label)
        .map((t: ddInterface) => t.id) as (string | number)[];
    });
    return unique;
  }

  getDropdownsItemIdsFromUniqueItems(
    ddList: ddInterface[],
    selectedItems: ddInterface[]
  ): (string | number)[] {
    return ddList
      .filter((item) => selectedItems.some((sel) => sel.id === item.id))
      .map((item) => item.ids)
      .flat()
      .filter((id): id is string | number => id !== undefined && id !== null);
  }

  getLast12Months(): {
    year: number;
    monthNumber: number;
    month: string;
    valueCount: number;
  }[] {
    const result: {
      year: number;
      monthNumber: number;
      month: string;
      valueCount: number;
    }[] = [];
    const today = new Date();
    const currentYear = today.getFullYear();

    // Array of month short names
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = date.getFullYear();
      const monthName = monthNames[date.getMonth()];
      result.push({
        year: year,
        monthNumber: date.getMonth() + 1,
        month: year == currentYear ? monthName : `${monthName}, ${year}`,
        valueCount: 0,
      });
    }
    return result;
  }

  mapApiDataToChartData(
    apiData: { year: number; month: number; total: number }[]
  ): ChartItem[] {
    const initialData = this.getLast12Months();
    const mappedData = initialData.map((item) => {
      const apiItem = apiData.find(
        (apiEntry) =>
          apiEntry.year === item.year && apiEntry.month === item.monthNumber
      );
      return {
        ...item,
        valueCount: apiItem ? apiItem.total : 0,
      };
    });
    return mappedData;
  }

  mapApiDataToClusterChartData(
    apiData: {
      year: number;
      month: number;
      open_count: number;
      close_count: number;
    }[]
  ): ClusterChartItem[] {
    const initialData = this.getLast12Months();
    const mappedData = initialData.map((item) => {
      const apiItem = apiData.find(
        (apiEntry) =>
          apiEntry.year === item.year && apiEntry.month === item.monthNumber
      );
      return {
        ...item,
        openValueCount: apiItem ? apiItem.open_count : 0,
        closedValueCount: apiItem ? apiItem.close_count : 0,
      };
    });
    return mappedData;
  }

  deepClone<T>(x: T): T {
    return JSON.parse(JSON.stringify(x));
  }

  convertUTCToLocal(dateStr: string): Date {
    const utcDate = new Date(dateStr.replace(' ', 'T'));
    return utcDate;
  }
}

export type notificationType =
  | 'none'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export interface ddInterface {
  id: string | number | null;
  label: string;
  ids?: (string | number)[];
}

export interface ChartItem {
  year: number;
  monthNumber: number;
  month: string;
  valueCount: number;
}

export interface ClusterChartItem {
  year: number;
  monthNumber: number;
  month: string;
  openValueCount: number;
  closedValueCount: number;
}
